import React, { useState, useEffect } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";

import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { setApiResult, resetApiResult } from "~/core/api/actions";
import { getApiError } from "~/core/api/selectors";
import { DialogBox, DialogBoxFooterType } from "~/core";
import { needs } from "~/hocs";
import { getConfirmMessages, getErrorMessages } from "~/i18n-error-messages";
import { messages } from "~/i18n-messages";
import { ErrorDialog } from "~/notifications/components/err-dialog";

import { AppHelpers, APIHelpers, FormattingHelpers, APIError, IErrorFile } from "@ai360/core";

import "./api-results-wrapper.css";

type IApiResultsWrapperProps = {
    children: React.ReactNode;
    clearErrorOnUnmount?: boolean;
    actionCallback?: () => void;
    intl: intlShape;
};

const ApiResultsWrapper = ({
    children,
    clearErrorOnUnmount = true,
    actionCallback = null,
    intl,
}: IApiResultsWrapperProps) => {
    const dispatch = useAppDispatch();
    const apiError = useAppSelector(getApiError);

    const [showDialogBox, setShowDialogBox] = useState(false);
    const [showServerError, setShowServerError] = useState(false);
    const [showErrorFileDownload, setShowErrorFileDownload] = useState(false);
    const [errorFile, setErrorFile] = useState<IErrorFile>(null);
    const [errors, setErrors] = useState(null);

    const resetApiResultHandler = () => {
        dispatch(
            setApiResult({
                errorCodeList: [],
                model: {},
                apiResultObj: {},
            })
        );
    };

    const onToggleModalClick = (newValue) => {
        setShowDialogBox(newValue);
        setShowServerError(newValue);
        setErrors(null);
        setShowErrorFileDownload(!errorFile ? false : true);
        resetApiResultHandler();
    };

    const onConfirmClick = () => {
        setShowDialogBox(false);
        setErrors(null);

        if (actionCallback && needs) {
            needs([actionCallback()], useAppDispatch);
        }
    };

    const downloadErrorFile = () => {
        const { base64Data, fileName } = errorFile;
        APIHelpers.downloadBlob(
            base64Data,
            `${fileName}${FormattingHelpers.getTimeStamp()}`,
            "txt"
        );
        onToggleErrorFileModal(false);
        setErrorFile(null);
        dispatch(
            setApiResult({
                errorCodeList: [],
                model: {},
                errorFile: null,
            })
        );
    };

    const onToggleErrorFileModal = (newValue) => {
        setErrorFile(null);
        setShowErrorFileDownload(newValue);
        dispatch(setApiResult({ errorFile: null }));
    };

    useEffect(() => {
        if (apiError.errorCodeList && apiError.errorCodeList.length > 0) {
            const errorMessages = getErrorMessages(intl.formatMessage, apiError, "<br/>");
            setShowDialogBox(true);
            setErrors({ __html: errorMessages });
        } else if (apiError.model && !AppHelpers.isEmptyObject(apiError.model)) {
            const { addCount, updateCount } = apiError.model;
            const errorMessages = getConfirmMessages(intl.formatMessage, "validImport", {
                addCount,
                updateCount,
            });
            setShowDialogBox(true);
            setErrors({ __html: errorMessages });
        } else if (
            apiError.apiResultObj &&
            apiError.apiResultObj.status &&
            (apiError.apiResultObj.exceptionMessage || apiError.apiResultObj.message)
        ) {
            setShowServerError(true);
            setErrors(apiError.apiResultObj);
        }

        if (apiError.errorFile && apiError.errorFile !== errorFile) {
            setErrorFile(apiError.errorFile);
        }
    }, [apiError, intl.formatMessage, errorFile]);

    useEffect(() => {
        if (clearErrorOnUnmount) {
            return () => {
                dispatch(resetApiResult());
            };
        }
    }, [clearErrorOnUnmount, dispatch]);

    const { formatMessage } = intl;
    const isConfirmModal =
        actionCallback &&
        apiError.model &&
        typeof apiError.model === "object" &&
        (apiError.model.updateCount || apiError.model.addCount);
    const count = apiError && apiError.errorCodeList ? apiError.errorCodeList.length : 0;
    const title = isConfirmModal
        ? formatMessage(messages.confirmTitle)
        : formatMessage(messages.errorsTitle, { count });

    return (
        <div className="content-table-container api-result-wrapper">
            <DialogBox
                footerType={isConfirmModal ? DialogBoxFooterType.YES_NO : DialogBoxFooterType.NONE}
                isOpen={showDialogBox}
                onAction={() => onConfirmClick()}
                onClose={() => onToggleModalClick(false)}
                title={title}
            >
                <div dangerouslySetInnerHTML={errors} />
            </DialogBox>
            <ErrorDialog
                isOpen={showServerError}
                err={new APIError("", errors)}
                intl={intl}
                retryAction={null}
                onRetry={() => null}
                onClose={() => onToggleModalClick(false)}
            />
            <DialogBox
                footerType={DialogBoxFooterType.YES_NO}
                isOpen={showErrorFileDownload}
                onAction={() => downloadErrorFile()}
                onClose={() => onToggleErrorFileModal(false)}
                title={title}
            >
                {formatMessage(messages.saveErrorTextFile)}
            </DialogBox>
            {children}
        </div>
    );
};

export default injectIntl(ApiResultsWrapper);
